import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import useHandleMouseMove from '../hooks/useHandleMouseMove';

/**
 * Thin wrapper around FullCalendar v6.
 *
 * Responsibilities:
 * - configure required plugins
 * - provide a small, stable prop surface for our views
 *
 * Business logic/data shaping must live in hooks and view components.
 */
const CalendarShell = ({
  calendarRef,
  initialView,
  initialDate,
  events,
  eventOrder = "start,-duration,title",
  onEventClick,
  onDateRangeChange,
  renderEventContent,
  height = 1080,
  contentHeight = 1080,
  slotDuration = '02:00:00',
  allDaySlot = false,
  slotEventOverlap = true,
  eventOverlap = true,
  minTime = '00:00:00',
  maxTime = '24:00:00',
  locale,
  nowIndicator = true,
  slotLabelFormat,
  handleWindowResize = false,
  dayHeaders = false,
  expandRows = true,
  eventClassNames,
  eventDidMount,
  dayHeaderContent,
  viewDidMount,
}) => {
  const restParameters = useMemo(() => {
    const params = {};
    if (eventClassNames) params.eventClassNames = eventClassNames;
    if (eventDidMount) params.eventDidMount = eventDidMount;
    if (dayHeaderContent) params.dayHeaderContent = dayHeaderContent;
    if (viewDidMount) params.viewDidMount = viewDidMount;
    return params;
  }, [eventClassNames, eventDidMount, dayHeaderContent, viewDidMount]);

  useHandleMouseMove(calendarRef.current);

  return (
    <FullCalendar
      ref={calendarRef}
      plugins={[timeGridPlugin, dayGridPlugin]}
      droppable={true}
      editable={false}
      eventStartEditable={false}
      eventDurationEditable={false}    
      initialView={initialView}
      initialDate={initialDate}
      events={events}
      eventOrder={eventOrder}
      eventContent={renderEventContent}
      eventClick={onEventClick}
      eventDisplay="block"
      datesSet={onDateRangeChange}
      slotDuration={slotDuration}
      height={height}
      contentHeight={contentHeight}
      headerToolbar={false}
      handleWindowResize={handleWindowResize}
      allDaySlot={allDaySlot}
      slotEventOverlap={slotEventOverlap}
      eventOverlap={eventOverlap}
      slotMinTime={minTime}
      slotMaxTime={maxTime}
      locale={locale}
      nowIndicator={nowIndicator}
      slotLabelFormat={slotLabelFormat}
      dayHeaders={dayHeaders}
      expandRows={expandRows}
      displayEventTime={false}
      {...restParameters}
    />
  );
};

CalendarShell.propTypes = {
  calendarRef: PropTypes.object,
  initialView: PropTypes.string.isRequired,
  initialDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  events: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  eventOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onEventClick: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  renderEventContent: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contentHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  slotDuration: PropTypes.string,
  allDaySlot: PropTypes.bool,
  slotEventOverlap: PropTypes.bool,
  eventOverlap: PropTypes.bool,
  minTime: PropTypes.string,
  maxTime: PropTypes.string,
  locale: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  nowIndicator: PropTypes.bool,
  slotLabelFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
  handleWindowResize: PropTypes.bool,
  dayHeaders: PropTypes.bool,
  expandRows: PropTypes.bool,
  eventClassNames: PropTypes.func,
  eventDidMount: PropTypes.func,
  dayHeaderContent: PropTypes.func,
  viewDidMount: PropTypes.func,
};

export default CalendarShell;

